import {
  Language,
  Service,
  View as ViewData,
  ViewMeta,
} from '@yleisradio/areena-types';
import { FavoriteUpdate } from 'components/FavoriteUpdate';
import { HeaderImageAspectRatio } from 'components/Header/types';
import { ViewMetadata } from 'components/Metadata';
import { View } from 'components/View';
import { GetServerSideProps } from 'next';
import React, { FunctionComponent, useMemo } from 'react';
import { fetchSlugs, getView } from 'services/areena-api/fetchers';
import { clientPathToViewPath } from 'services/areena-api/path';
import { filterViewByAudience } from 'utils/content';
import { getItemService, isRadioItem } from 'utils/item';
import { isAuthenticated } from 'utils/login';
import {
  getItemRedirect,
  getLegacyLivePageRedirect,
  getPersonalPageRedirect,
} from 'utils/redirects';
import { AnalyticsContextProvider } from 'contexts/AnalyticsContext';
import { useRouter } from 'next/router';
import { ReferrerProvider } from 'contexts/ReferrerContext';
import { getSchemaOrgNodes } from 'services/programs-api/fetchers';
import { SchemaOrgNode } from 'services/schema-org/types';
import {
  getItemIdFromClientPath,
  isPackageViewPathWithSlug,
  serviceFromClientPath,
} from 'utils/url';
import { useAreenaService } from 'contexts/AreenaServiceContext';
import { useIsomorphicLayoutEffect } from 'hooks/useIsomorphicLayoutEffect';
import apm from 'elastic-apm-node';

type Props = {
  headerImageAspectRatio: HeaderImageAspectRatio;
  meta: ViewMeta;
  path: string;
  userIsAuthenticated: boolean;
  view: ViewData;
  selectedTab: string | null;
  metaImageAspectRatio: '16:9' | '1:1';
  referrer: string | null;
  schemaOrgNodes: SchemaOrgNode[];
  slugs: Record<string, string> | null;
  areenaService: Service | null;
};

const Page: FunctionComponent<Props> = ({
  headerImageAspectRatio,
  meta,
  path,
  userIsAuthenticated,
  view,
  selectedTab,
  metaImageAspectRatio,
  referrer,
  schemaOrgNodes,
  slugs,
  areenaService,
}) => {
  const { query } = useRouter();

  const filteredViewData = useMemo(
    () => filterViewByAudience(view, userIsAuthenticated),
    [view, userIsAuthenticated]
  );

  const { setAreenaService } = useAreenaService();

  useIsomorphicLayoutEffect(() => {
    setAreenaService(areenaService ?? undefined);
  }, [areenaService, setAreenaService]);

  return (
    <>
      <ViewMetadata
        path={path}
        query={query}
        schemaOrgNodes={schemaOrgNodes}
        view={view}
        viewMeta={meta}
        metaImageAspectRatio={metaImageAspectRatio}
        slugs={slugs}
      />
      <FavoriteUpdate item={meta.item} />
      <ReferrerProvider referrer={referrer || undefined}>
        <AnalyticsContextProvider context={view.analytics?.context?.comscore}>
          <View
            headerImageAspectRatio={headerImageAspectRatio}
            view={filteredViewData}
            viewKey={path}
            selectedTab={selectedTab}
            key={path}
          />
        </AnalyticsContextProvider>
      </ReferrerProvider>
    </>
  );
};

export default Page;

export const getServerSideProps: GetServerSideProps<Props> = async ({
  locale,
  req,
  res,
  resolvedUrl,
}) => {
  apm.setTransactionName('GET [...path]');

  const {
    cookies,
    headers: { cookie: cookieHeader, referer: referrer = null },
  } = req;

  const {
    pathname: path,
    search: query,
    searchParams: queryParams,
  } = new URL(resolvedUrl, 'https://localhost/');
  const language: Language = locale === 'sv' ? 'sv' : 'fi';

  const personalPageRedirect = getPersonalPageRedirect(path, language);

  if (personalPageRedirect) {
    return { redirect: { destination: personalPageRedirect, permanent: true } };
  }

  const legacyLivePageRedirect = getLegacyLivePageRedirect(path, language);

  if (legacyLivePageRedirect) {
    res.setHeader('Cache-Control', 'max-age=60');
    return {
      redirect: { destination: legacyLivePageRedirect, permanent: true },
    };
  }

  const service = serviceFromClientPath(path);

  const slugs = isPackageViewPathWithSlug(path)
    ? await fetchSlugs(service, language).then((res) => res?.data)
    : undefined;

  const apiPath = clientPathToViewPath(path, {
    slugs,
  });

  const itemId = getItemIdFromClientPath(path);

  if (apiPath) {
    const [apiResponse, schemaOrgNodes] = await Promise.all([
      getView({
        path: apiPath,
        language,
        requestCookie: cookieHeader,
      }),
      itemId ? getSchemaOrgNodes(itemId, language) : [],
    ]);

    if (apiResponse) {
      const { cacheControl, setCookieHeaders, meta, view } = apiResponse;
      const { item } = meta;

      if (cacheControl) {
        res.setHeader('Cache-Control', cacheControl);
      }
      if (setCookieHeaders) {
        res.setHeader('Set-Cookie', setCookieHeaders);
      }

      const pageRedirect = getItemRedirect(
        path,
        language,
        meta,
        view.presentation
      );

      if (pageRedirect) {
        return redirectWithQuery(pageRedirect, query);
      }

      res.setHeader('Vary', 'Cookie');

      const isPodcast = item && isRadioItem(item);
      const headerImageAspectRatio = isPodcast ? '1:1' : '16:9';
      const metaImageAspectRatio = isPodcast ? '1:1' : '16:9';

      const selectedTab = queryParams.get('t');

      const areenaService =
        getAreenaService(path) ?? (item && getItemService(item));

      return {
        props: {
          view,
          meta,
          path,
          userIsAuthenticated: isAuthenticated(cookies),
          headerImageAspectRatio,
          selectedTab,
          metaImageAspectRatio,
          referrer,
          schemaOrgNodes,
          slugs: slugs ?? null,
          areenaService: areenaService ?? null,
        } satisfies Props,
      };
    }
  }

  return { notFound: true };
};

function redirectWithQuery(path: string, query: string) {
  return { redirect: { destination: `${path}${query}`, permanent: true } };
}

function getAreenaService(path: string): Service | null {
  if (path.startsWith('/tv')) return 'tv';

  if (path.startsWith('/podcastit') || path.startsWith('/poddar'))
    return 'radio';

  return null;
}
